import React from 'react';
import { Switch } from '@roger/ui-components';

import { useAdminSectionAccess } from '../../hooks/useAdminSectionAccess';
import type { AgencyConfigPayload } from '../../services/agencyConfig';
import { useAgencyConfigSection } from '../../hooks/useAgencyConfigSection';
import { SettingsEditFooter, SettingValue } from './SettingsSectionParts';

type ChartWorkflowKey =
  | 'allow_submit_without_signature'
  | 'require_clean_oasis_for_clinician_signature'
  | 'require_clean_oasis_for_qa_signature'
  | 'require_clean_oasis_for_export';

type ChartWorkflowValues = Record<ChartWorkflowKey, boolean>;

const SETTINGS: {
  key: ChartWorkflowKey;
  label: string;
  description: string;
  defaultValue: boolean;
}[] = [
  {
    key: 'allow_submit_without_signature',
    label: 'Submit without signing',
    description:
      'Clinicians can submit a chart before signing it. Case managers can return one without approving. The signature is still owed.',
    defaultValue: true,
  },
  {
    key: 'require_clean_oasis_for_clinician_signature',
    label: 'Require clinicians to fix OASIS transmission errors before signing',
    description:
      'Clinicians cannot sign an OASIS chart until the All CMS scrubber shows no errors.',
    defaultValue: false,
  },
  {
    key: 'require_clean_oasis_for_qa_signature',
    label: 'Require QA to fix OASIS transmission errors before signing',
    description:
      'QA cannot sign or complete an OASIS chart until the All CMS scrubber shows no errors.',
    defaultValue: true,
  },
  {
    key: 'require_clean_oasis_for_export',
    label: 'Block exporting OASIS charts that CMS will reject',
    description:
      'The OASIS Manager will not export a completed chart until the All CMS scrubber shows no errors, and shows which questions to fix.',
    defaultValue: true,
  },
];

const DEFAULTS = Object.fromEntries(
  SETTINGS.map(s => [s.key, s.defaultValue])
) as ChartWorkflowValues;

function fromConfig(cfg: AgencyConfigPayload): ChartWorkflowValues {
  return Object.fromEntries(
    SETTINGS.map(s => [s.key, cfg[s.key] ?? s.defaultValue])
  ) as ChartWorkflowValues;
}

// Only the toggles that moved. The same endpoint also carries the Intake
// Manager column layout, which this screen never edits and must not reset.
function diff(
  values: ChartWorkflowValues,
  saved: ChartWorkflowValues
): Partial<ChartWorkflowValues> {
  return Object.fromEntries(
    SETTINGS.filter(s => values[s.key] !== saved[s.key]).map(s => [
      s.key,
      values[s.key],
    ])
  ) as Partial<ChartWorkflowValues>;
}

/**
 * Agency policy for how charts move between clinicians and QA: whether a chart
 * can advance without a signature (ROG-3268), and whether an OASIS chart must
 * pass the All CMS scrubber before it can be signed or exported.
 *
 * These are the agency's call, not rollout gates, which is why they sit on the
 * agency Admin Panel rather than Internal Admin and why they are stored on
 * AgencyConfig rather than behind a feature flag.
 */
const ChartWorkflowSection: React.FC = () => {
  const { canEdit } = useAdminSectionAccess('chart-workflow');
  const section = useAgencyConfigSection({
    enabled: true,
    initial: DEFAULTS,
    fromConfig,
    diff,
    successMessage: 'Chart workflow settings updated',
    errorMessage: 'Failed to update chart workflow settings',
  });
  const { values, setValues, isLoading, loadFailed } = section;
  const showDisplayView = !section.isEditing || !canEdit;

  return (
    <div className='space-y-6'>
      <div className='space-y-1'>
        <p className='text-sm text-muted-foreground'>
          How charts move between clinicians and case managers
        </p>
        {loadFailed && (
          <p className='text-xs text-destructive'>
            These settings did not load. Reload the page to see them.
          </p>
        )}
      </div>
      <div>
        <div className='space-y-6'>
          {SETTINGS.map(setting => (
            <div
              key={setting.key}
              data-testid={`setting-${setting.key}`}
              className='flex items-center justify-between gap-4 rounded-md border p-3'
            >
              <div className='space-y-0.5'>
                <label htmlFor={setting.key} className='text-sm font-medium'>
                  {setting.label}
                </label>
                <p className='text-xs text-muted-foreground'>
                  {setting.description}
                </p>
              </div>
              <SettingValue isLoading={isLoading} loadFailed={loadFailed}>
                {!showDisplayView ? (
                  <Switch
                    id={setting.key}
                    checked={values[setting.key]}
                    onCheckedChange={checked =>
                      setValues(prev => ({ ...prev, [setting.key]: checked }))
                    }
                  />
                ) : (
                  <span
                    className={`shrink-0 whitespace-nowrap text-sm font-medium ${
                      values[setting.key]
                        ? 'text-green-600'
                        : 'text-muted-foreground'
                    }`}
                  >
                    {values[setting.key] ? 'Enabled' : 'Disabled'}
                  </span>
                )}
              </SettingValue>
            </div>
          ))}

          <SettingsEditFooter
            canEdit={canEdit}
            isEditing={section.isEditing}
            isSaving={section.isSaving}
            editDisabled={isLoading || loadFailed}
            onEdit={section.startEditing}
            onCancel={section.cancel}
            onSave={() => void section.save()}
          />
        </div>
      </div>
    </div>
  );
};

export default ChartWorkflowSection;
